/**
 * ============================================
 * USE CCU HOOK
 * ============================================
 * 
 * Fetches CCU (Concurrent Users) metrics from the backend.
 * Includes current CCU value and historical data for graphs.
 * Also handles CCU threshold alarms with local storage tracking.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getCCUMetrics } from '@/services/api';

export interface CCUDataPoint {
  timestamp: number;
  value: number;
}

export interface UseCCUResult {
  currentCCU: number;
  ccuHistory: CCUDataPoint[];
  duration: string;
  isLoading: boolean;
  error: string | null;
  refetch: (newDuration?: string) => void;
}

// CCU thresholds that trigger alarms
const CCU_THRESHOLDS = [100, 250, 500, 1500, 2500, 4000, 5000, 7500, 9000, 10000];

// Local storage key for acknowledged CCU thresholds
const CCU_ACKNOWLEDGED_KEY = 'lokko_ccu_acknowledged_threshold';

/**
 * Get the highest acknowledged CCU threshold from local storage
 */
function getAcknowledgedThreshold(): number {
  try {
    const stored = localStorage.getItem(CCU_ACKNOWLEDGED_KEY);
    if (stored) {
      const value = parseInt(stored, 10);
      if (!isNaN(value)) return value;
    }
  } catch (e) {
    console.error('Failed to read CCU acknowledged threshold:', e);
  }
  return 0;
}

/**
 * Save the acknowledged CCU threshold to local storage
 */
function setAcknowledgedThreshold(threshold: number): void {
  try {
    localStorage.setItem(CCU_ACKNOWLEDGED_KEY, threshold.toString());
  } catch (e) {
    console.error('Failed to save CCU acknowledged threshold:', e);
  }
}

/**
 * Get the next threshold that should trigger an alarm based on current CCU
 * Returns null if no new threshold is crossed
 */
export function getNextCCUThreshold(currentCCU: number, acknowledgedThreshold: number): number | null {
  // Find thresholds that are:
  // 1. Less than or equal to current CCU (crossed)
  // 2. Greater than the already acknowledged threshold (not yet acknowledged)
  const crossedThresholds = CCU_THRESHOLDS.filter(
    t => currentCCU >= t && t > acknowledgedThreshold
  );
  
  // Return the highest crossed threshold that hasn't been acknowledged
  return crossedThresholds.length > 0 ? Math.max(...crossedThresholds) : null;
}

/**
 * Check if CCU alarm should trigger and handle acknowledgment
 */
export function useCCUAlarm(currentCCU: number): {
  shouldAlarm: boolean;
  thresholdCrossed: number | null;
  acknowledgeAlarm: () => void;
} {
  const [acknowledgedThreshold, setAcknowledged] = useState<number>(() => getAcknowledgedThreshold());
  const [thresholdCrossed, setThresholdCrossed] = useState<number | null>(null);
  const lastCheckedCCU = useRef<number>(0);
  
  useEffect(() => {
    // Only check if CCU has changed significantly
    if (Math.abs(currentCCU - lastCheckedCCU.current) < 10) return;
    lastCheckedCCU.current = currentCCU;
    
    const nextThreshold = getNextCCUThreshold(currentCCU, acknowledgedThreshold);
    setThresholdCrossed(nextThreshold);
  }, [currentCCU, acknowledgedThreshold]);
  
  const acknowledgeAlarm = useCallback(() => {
    if (thresholdCrossed) {
      setAcknowledgedThreshold(thresholdCrossed);
      setAcknowledged(thresholdCrossed);
      setThresholdCrossed(null);
    }
  }, [thresholdCrossed]);
  
  return {
    shouldAlarm: thresholdCrossed !== null,
    thresholdCrossed,
    acknowledgeAlarm,
  };
}

export function useCCU(initialDuration = '1h'): UseCCUResult {
  const [currentCCU, setCurrentCCU] = useState<number>(0);
  const [ccuHistory, setCCUHistory] = useState<CCUDataPoint[]>([]);
  const [duration, setDuration] = useState<string>(initialDuration);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (fetchDuration: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getCCUMetrics(fetchDuration);
      
      if (data.error) {
        setError(data.error);
      } else {
        // Convert history to timestamp numbers for chart
        const history = (data.ccu_history || []).map(point => ({
          timestamp: new Date(point.timestamp).getTime(),
          value: point.value,
        }));
        
        setCCUHistory(history);
        
        // Use the LAST value from history as current CCU (most recent data point)
        if (history.length > 0) {
          setCurrentCCU(history[history.length - 1].value);
        } else {
          setCurrentCCU(0);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch CCU data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchData(duration);
    
    // Poll every 30 seconds
    const interval = setInterval(() => {
      fetchData(duration);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [duration, fetchData]);

  const refetch = useCallback((newDuration?: string) => {
    if (newDuration && newDuration !== duration) {
      setDuration(newDuration);
    } else {
      fetchData(duration);
    }
  }, [duration, fetchData]);

  return {
    currentCCU,
    ccuHistory,
    duration,
    isLoading,
    error,
    refetch,
  };
}
